import type { Api } from '../../../lib/gramjs';
import type { TypedBroadcastChannel } from '../../../util/browser/multitab';
import type { ShellLifecycleEvent } from '../../../util/browser/shellContainer';
import type { ApiInitialArgs, ApiOnProgress, OnApiUpdate } from '../../types';
import type { LocalDb } from '../localDb';
import type { MethodArgs, MethodResponse, Methods } from '../methods/types';
import type {
  OriginPayload, OriginRequestPayload, ThenArg, WorkerMessageEvent,
} from './types';

import { DEBUG, IGNORE_UNHANDLED_ERRORS } from '../../../config';
import { IS_SHELL, subscribeToShellLifecycle } from '../../../util/browser/shellContainer';
import { recordClientDiagnosticEvent, shouldRecordUpdateBatch } from '../../../util/clientDiagnostics';
import { logDebugMessage } from '../../../util/debugConsole';
import Deferred from '../../../util/Deferred';
import { getCurrentTabId, subscribeToMasterChange } from '../../../util/establishMultitabRole';
import generateUniqueId from '../../../util/generateUniqueId';
import { ACCOUNT_SLOT, DATA_BROADCAST_CHANNEL_NAME } from '../../../util/multiaccount';
import { pause, throttleWithTickEnd } from '../../../util/schedulers';
import { markStartupPerf } from '../../../util/startupPerf';

type RequestState = {
  messageId: string;
  resolve: Function;
  reject: Function;
  callback?: AnyToVoidFunction;
  DEBUG_payload?: any;
  startedAt?: number;
  via?: 'worker' | 'master';
};

const HEALTH_CHECK_TIMEOUT = 4000;
const HEALTH_CHECK_RETRY_DELAY = 1000;
const HEALTH_CHECK_MISSES_BEFORE_KILL = 2;
const WORKER_WARMUP_MS = 10 * 1000;
const HEALTH_CHECK_MIN_DELAY = 5 * 1000;
const NO_QUEUE_BEFORE_INIT = new Set(['destroy']);

export const WORKER_TERMINATED_ERROR = 'WORKER_TERMINATED';

let worker: Worker | undefined;
let workerCreatedAt = 0;
let initGeneration = 0;
let isHealthCheckAttached = false;
let unsubscribeFromShellLifecycle: NoneToVoidFunction | undefined;

const requestStates = new Map<string, RequestState>();
const requestStatesByCallback = new Map<AnyToVoidFunction, RequestState>();

let pendingPayloads: OriginPayload[] = [];

const savedLocalDb: LocalDb = {
  chats: {},
  users: {},
  documents: {},
  photos: {},
  webDocuments: {},
  commonBoxState: {},
  channelPtsById: {},
};

let isMasterTab = true;
subscribeToMasterChange((isMasterTabNew) => {
  isMasterTab = isMasterTabNew;
});

let isFirstWorkerMessageMarked = false;
let isFirstApiResponseMarked = false;

const channel = new BroadcastChannel(DATA_BROADCAST_CHANNEL_NAME) as TypedBroadcastChannel;

const postMessagesOnTickEnd = throttleWithTickEnd(() => {
  const payloads = pendingPayloads;
  pendingPayloads = [];
  worker?.postMessage({ payloads });
});

function postMessageOnTickEnd(payload: OriginPayload) {
  pendingPayloads.push(payload);
  postMessagesOnTickEnd();
}

export function initApiOnMasterTab(initialArgs: ApiInitialArgs) {
  channel.postMessage({
    type: 'initApi',
    token: getCurrentTabId(),
    initialArgs,
  });
}

let updateCallback: OnApiUpdate;

let localApiRequestsQueue: { fnName: any; args: any; deferred: Deferred<any> }[] = [];
let apiRequestsQueue: { fnName: any; args: any; deferred: Deferred<any> }[] = [];
let isInited = false;
let isWorkerWired = false;
let isInitializing = false;

function createApiWorker() {
  if (worker) return;

  markStartupPerf('worker_boot_start');

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('>>> START LOAD WORKER');
  }

  const params = new URLSearchParams();
  if (ACCOUNT_SLOT) {
    params.set('account', String(ACCOUNT_SLOT));
  }

  worker = new Worker(new URL('./worker.ts', import.meta.url), {
    name: params.toString(),
  });
  workerCreatedAt = Date.now();
}

// 提前建 worker，让它的脚本解析和主线程的 initGlobal / 首屏渲染并行。
// 线上冷启动中位 1560ms，其中 worker_boot_start(1056) → worker_first_message(1363)
// 这 307ms 完全串在关键路径上，而 api_first_response 又卡着 ui_loader_ready。
// 只建实例、不订阅消息：生产环境下 worker 在收到 initApi 前不会主动 postMessage，不会丢消息。
export function warmUpApiWorker() {
  createApiWorker();
}

export function initApi(onUpdate: OnApiUpdate, initialArgs: ApiInitialArgs) {
  updateCallback = onUpdate;

  if (!isMasterTab) {
    initApiOnMasterTab(initialArgs);
    return Promise.resolve();
  }

  if (isInitializing) {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.warn('[Connector] initApi() already in progress, skipping');
    }
    return Promise.resolve();
  }

  createApiWorker();

  if (!isWorkerWired) {
    isWorkerWired = true;
    subscribeToWorker(onUpdate);

    document.addEventListener('visibilitychange', handleVisibilityChangeForWorker);
    window.addEventListener('pageshow', handlePageShowForWorker);
    document.addEventListener('resume', handlePageResumeForWorker);
    document.addEventListener('freeze', handlePageFreezeForWorker);
    window.addEventListener('online', handleOnlineForWorker);
    unsubscribeFromShellLifecycle = subscribeToShellLifecycle(handleShellLifecycleEvent);

    if (initialArgs.platform === 'iOS' || IS_SHELL) {
      setupWorkerHealthCheck();
    }
  }

  isInitializing = true;
  const myGeneration = initGeneration;
  return makeRequest({
    type: 'initApi',
    args: [initialArgs, savedLocalDb],
  }).then(() => {
    if (myGeneration !== initGeneration) return;

    markFirstApiResponse({ type: 'initApi' });
    isInited = true;
    isInitializing = false;

    apiRequestsQueue.forEach((request) => {
      callApi(request.fnName, ...request.args)
        .then(request.deferred.resolve)
        .catch(request.deferred.reject);
    });
    apiRequestsQueue = [];

    localApiRequestsQueue.forEach((request) => {
      callApiLocal(request.fnName, ...request.args)
        .then(request.deferred.resolve)
        .catch(request.deferred.reject);
    });
    localApiRequestsQueue = [];
  }).catch((err: any) => {
    if (myGeneration !== initGeneration) throw err;

    isInitializing = false;
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.error('[Connector] initApi failed:', err);
    }
    throw err;
  });
}

export async function destroyApi() {
  if (!worker) return;

  try {
    await makeRequest({ type: 'callMethod', name: 'destroy', args: [true, true] });
  } catch (_) {
    // ignore - worker may already be dead
  }

  worker.terminate();
  worker = undefined;
  workerCreatedAt = 0;
  isWorkerWired = false;
  isInited = false;
  isInitializing = false;
  initGeneration += 1;

  apiRequestsQueue.forEach((request) => request.deferred.reject(new Error('API_DESTROYED')));
  localApiRequestsQueue.forEach((request) => request.deferred.reject(new Error('API_DESTROYED')));
  apiRequestsQueue = [];
  localApiRequestsQueue = [];

  rejectInflightWorkerRequests('API_DESTROYED');
  requestStates.clear();
  requestStatesByCallback.clear();
  pendingPayloads = [];

  document.removeEventListener('visibilitychange', handleVisibilityChangeForWorker);
  window.removeEventListener('pageshow', handlePageShowForWorker);
  document.removeEventListener('resume', handlePageResumeForWorker);
  document.removeEventListener('freeze', handlePageFreezeForWorker);
  window.removeEventListener('online', handleOnlineForWorker);
  window.removeEventListener('focus', handleFocusForHealthCheck);
  document.removeEventListener('visibilitychange', handleVisibilityForHealthCheck);
  unsubscribeFromShellLifecycle?.();
  unsubscribeFromShellLifecycle = undefined;
  isHealthCheckAttached = false;

  if (pendingReconnectTimer) {
    clearTimeout(pendingReconnectTimer);
    pendingReconnectTimer = null;
  }

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('[Connector] Worker destroyed and states reset');
  }
}

export function updateLocalDb(name: keyof LocalDb, prop: string, value: any) {
  savedLocalDb[name][prop] = value;
}

export function updateFullLocalDb(initial: LocalDb) {
  Object.assign(savedLocalDb, initial);
}

export function callApiOnMasterTab(payload: any) {
  channel.postMessage({
    type: 'callApi',
    token: getCurrentTabId(),
    ...payload,
  });
}

export function setShouldEnableDebugLog(value: boolean) {
  return makeRequest({
    type: 'toggleDebugMode',
    isEnabled: value,
  });
}

export function callApiLocal<T extends keyof Methods>(fnName: T, ...args: MethodArgs<T>) {
  if (!isInited) {
    if (NO_QUEUE_BEFORE_INIT.has(fnName)) {
      return Promise.resolve(undefined) as MethodResponse<T>;
    }

    const deferred = new Deferred();
    localApiRequestsQueue.push({ fnName, args, deferred });

    return deferred.promise as MethodResponse<T>;
  }

  const promise = makeRequest({
    type: 'callMethod',
    name: fnName,
    args,
  });

  if (DEBUG) {
    (async () => {
      try {
        type ForbiddenTypes =
          Api.VirtualClass<any>
          | (Api.VirtualClass<any> | undefined)[];
        type ForbiddenResponses =
          ForbiddenTypes
          | (AnyLiteral & { [k: string]: ForbiddenTypes });

        const response = await promise;
        const allowedResponse: Exclude<typeof response, ForbiddenResponses> = response;
        void allowedResponse;
      } catch (err) {
        // Do noting
      }
    })();
  }

  return promise as MethodResponse<T>;
}

export function callApi<T extends keyof Methods>(fnName: T, ...args: MethodArgs<T>) {
  if (!isInited && isMasterTab) {
    if (NO_QUEUE_BEFORE_INIT.has(fnName)) {
      return Promise.resolve(undefined) as MethodResponse<T>;
    }

    const deferred = new Deferred();
    apiRequestsQueue.push({ fnName, args, deferred });

    return deferred.promise as MethodResponse<T>;
  }

  const promise = isMasterTab ? makeRequest({
    type: 'callMethod',
    name: fnName,
    args,
  }) : makeRequestToMaster({
    name: fnName,
    args,
  });

  if (DEBUG) {
    (async () => {
      try {
        type ForbiddenTypes =
          Api.VirtualClass<any>
          | (Api.VirtualClass<any> | undefined)[];
        type ForbiddenResponses =
          ForbiddenTypes
          | (AnyLiteral & { [k: string]: ForbiddenTypes });

        const response = await promise;
        const allowedResponse: Exclude<typeof response, ForbiddenResponses> = response;
        void allowedResponse;
      } catch (err) {
        // Do noting
      }
    })();
  }

  return promise as MethodResponse<T>;
}

export function cancelApiProgress(progressCallback: ApiOnProgress) {
  progressCallback.isCanceled = true;

  const { messageId } = requestStatesByCallback.get(progressCallback) || {};
  if (!messageId) {
    return;
  }

  if (isMasterTab) {
    cancelApiProgressMaster(messageId);
  } else {
    channel.postMessage({
      type: 'cancelApiProgress',
      token: getCurrentTabId(),
      messageId,
    });
  }
}

export function cancelApiProgressMaster(messageId: string) {
  postMessageOnTickEnd({
    type: 'cancelProgress',
    messageId,
  });
}

function subscribeToWorker(onUpdate: OnApiUpdate) {
  worker?.addEventListener('message', ({ data }: WorkerMessageEvent) => {
    if (!isFirstWorkerMessageMarked) {
      isFirstWorkerMessageMarked = true;
      markStartupPerf('worker_first_message');
    }

    data?.payloads.forEach((payload) => {
      if (payload.type === 'updates') {
        const startedAt = performance.now();
        payload.updates.forEach(onUpdate);
        const duration = performance.now() - startedAt;

        if (shouldRecordUpdateBatch(payload.updates.length, duration)) {
          recordClientDiagnosticEvent({
            type: 'main_update_applied',
            reason: `count:${payload.updates.length}`,
            duration,
          });
        }

        if (DEBUG && duration > 5) {
          // eslint-disable-next-line no-console
          console.warn(`[API] Slow updates processing: ${payload.updates.length} updates in ${duration} ms`);
        }
      } else if (payload.type === 'methodResponse') {
        handleMethodResponse(payload);
      } else if (payload.type === 'methodCallback') {
        handleMethodCallback(payload);
      } else if (payload.type === 'unhandledError') {
        const raw = payload.error?.message;
        const message = typeof raw === 'string' ? raw : JSON.stringify(payload.error ?? raw ?? 'Unknown');
        if (message && IGNORE_UNHANDLED_ERRORS.has(message)) return;
        throw new Error(message);
      } else if (payload.type === 'debugLog') {
        logDebugMessage(payload.level, ...payload.args);
      } else if (payload.type === 'diagnosticEvent') {
        if (DEBUG && (payload.event.type === 'http_trace' || payload.event.type === 'transport_state')) {
          const { reason, error, duration } = payload.event;
          const suffix = `${error ? ` err:${error}` : ''}${duration ? ` ms:${Math.round(duration)}` : ''}`;
          // eslint-disable-next-line no-console
          console.info(`[HTTP] ${reason}${suffix}`);
        }
        recordClientDiagnosticEvent(payload.event);
      }
    });
  });
}

export function handleMethodResponse(data: {
  messageId: string;
  response?: ThenArg<MethodResponse<keyof Methods>>;
  error?: { message: string };
}) {
  const requestState = requestStates.get(data.messageId);
  if (requestState) {
    recordMethodDiagnostic(requestState, data.error?.message);
    if (data.error) {
      const msg = typeof data.error.message === 'string' ? data.error.message : JSON.stringify(data.error);
      requestState.reject(new Error(msg));
    } else {
      markFirstApiResponse(requestState.DEBUG_payload && 'name' in requestState.DEBUG_payload
        ? { name: requestState.DEBUG_payload.name }
        : undefined);
      requestState.resolve(data.response);
    }
  }
}

export function handleMethodCallback(data: {
  messageId: string;
  callbackArgs: any[];
}) {
  requestStates.get(data.messageId)?.callback?.(...data.callbackArgs);
}

function makeRequestToMaster(message: {
  messageId?: string;
  name: keyof Methods;
  args: MethodArgs<keyof Methods>;
  withCallback?: boolean;
}) {
  const messageId = generateUniqueId();
  const payload = {
    messageId,
    ...message,
  };

  const requestState = { messageId, startedAt: Date.now(), via: 'master' } as RequestState;

  const promise: Promise<unknown> = new Promise((resolve, reject) => {
    Object.assign(requestState, { resolve, reject });
  });

  if ('args' in payload && 'name' in payload && typeof payload.args[1] === 'function') {
    payload.withCallback = true;

    const callback = payload.args.pop() as AnyToVoidFunction;
    requestState.callback = callback;
    requestStatesByCallback.set(callback, requestState);
  }

  requestState.DEBUG_payload = { type: 'callMethod', name: payload.name };
  requestStates.set(messageId, requestState);

  promise
    .catch(() => undefined)
    .finally(() => {
      requestStates.delete(messageId);

      if (requestState.callback) {
        requestStatesByCallback.delete(requestState.callback);
      }
    });

  callApiOnMasterTab(payload);

  return promise;
}

function makeRequest(message: OriginRequestPayload) {
  return makeRequestWithId(message).promise;
}

function makeRequestWithId(message: OriginRequestPayload) {
  const messageId = generateUniqueId();
  const payload: OriginRequestPayload = {
    messageId,
    ...message,
  };

  const requestState = { messageId, startedAt: Date.now(), via: 'worker' } as RequestState;

  const promise: Promise<unknown> = new Promise((resolve, reject) => {
    Object.assign(requestState, { resolve, reject });
  });

  if ('args' in payload && 'name' in payload && typeof payload.args[1] === 'function') {
    payload.withCallback = true;

    const callback = payload.args.pop() as AnyToVoidFunction;
    requestState.callback = callback;
    requestStatesByCallback.set(callback, requestState);
  }

  requestState.DEBUG_payload = payload;

  requestStates.set(messageId, requestState);

  promise
    .catch(() => undefined)
    .finally(() => {
      requestStates.delete(messageId);

      if (requestState.callback && requestStatesByCallback.get(requestState.callback) === requestState) {
        requestStatesByCallback.delete(requestState.callback);
      }
    });

  postMessageOnTickEnd(payload);

  return { messageId, promise };
}

function describeSendMessage(requestState: RequestState) {
  const parts = [`via:${requestState.via || 'worker'}`];

  const args = (requestState.DEBUG_payload as { args?: unknown[] } | undefined)?.args;
  const params = Array.isArray(args) ? args[0] as Record<string, unknown> | undefined : undefined;

  if (params && typeof params === 'object') {
    const text = typeof params.text === 'string' ? params.text : '';
    parts.push(`url:${/(https?:\/\/|www\.|\b[a-z0-9-]+\.(com|net|org|me|cn|io|app)\b)/i.test(text) ? 1 : 0}`);
    if (params.attachment) parts.push('media:1');
  }

  return parts.join(' ');
}

function recordMethodDiagnostic(requestState: RequestState, error?: string) {
  const payload = requestState.DEBUG_payload;
  if (!payload || payload.type !== 'callMethod' || !requestState.startedAt) return;

  const duration = Date.now() - requestState.startedAt;
  const name = 'name' in payload ? String(payload.name) : '';

  const via = requestState.via || 'worker';

  if (name === 'sendMessage') {
    recordClientDiagnosticEvent({
      type: 'send_message_ack',
      reason: describeSendMessage(requestState),
      counterTag: via,
      duration,
      success: !error,
      error,
    });
  } else if (name === 'requestChannelDifference') {
    recordClientDiagnosticEvent({
      type: 'get_channel_difference',
      reason: `manual_request via:${via}`,
      counterTag: via,
      duration,
      success: !error,
      error,
    });
  }
}

function markFirstApiResponse(data?: Record<string, any>) {
  if (isFirstApiResponseMarked) return;

  isFirstApiResponseMarked = true;
  markStartupPerf('api_first_response', data);
}

const startedAt = Date.now();

let lastHiddenAt = 0;
let lastReconnectDispatchedAt = 0;
const RECONNECT_AFTER_HIDDEN_MS = IS_SHELL ? 1500 : 8000;
const RECONNECT_DISPATCH_COOLDOWN_MS = 25000;
const RECONNECT_DISPATCH_COOLDOWN_FAST_MS = 8000;
const FAST_COOLDOWN_REASON = 'online';
const FAST_COOLDOWN_REASON_PREFIX = 'shell-network:';

function getDispatchCooldownMs(reason: string) {
  if (reason === FAST_COOLDOWN_REASON || reason.startsWith(FAST_COOLDOWN_REASON_PREFIX)) {
    return RECONNECT_DISPATCH_COOLDOWN_FAST_MS;
  }

  return RECONNECT_DISPATCH_COOLDOWN_MS;
}

function sendForceReconnectToWorker(reason?: string) {
  if (!worker) return;
  postMessageOnTickEnd({ type: 'forceReconnect', reason });
  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log(`[Connector] Sent forceReconnect to worker, reason=${reason || 'unknown'}`);
  }
}

export function forceReconnectApi(reason?: string) {
  if (DEBUG && reason) {
    // eslint-disable-next-line no-console
    console.log(`[Connector] forceReconnectApi, reason=${reason}`);
  }
  sendForceReconnectToWorker(reason);
}

let pendingReconnectTimer: ReturnType<typeof setTimeout> | null = null;
const RECONNECT_DEBOUNCE_MS = IS_SHELL ? 0 : 500;

function scheduleForceReconnect(reason: string) {
  const hiddenDurationMs = lastHiddenAt ? Date.now() - lastHiddenAt : 0;
  if (hiddenDurationMs > 0 && hiddenDurationMs < RECONNECT_AFTER_HIDDEN_MS) {
    return;
  }

  const now = Date.now();
  if (now - lastReconnectDispatchedAt < getDispatchCooldownMs(reason)) {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log(`[Connector] forceReconnect cooldown-skip, reason=${reason}`);
    }
    return;
  }

  if (pendingReconnectTimer) {
    clearTimeout(pendingReconnectTimer);
  }
  pendingReconnectTimer = setTimeout(() => {
    pendingReconnectTimer = null;
    lastReconnectDispatchedAt = Date.now();
    lastHiddenAt = 0;
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log(`[Connector] forceReconnect dispatched, reason=${reason}`);
    }
    sendForceReconnectToWorker(reason);
  }, RECONNECT_DEBOUNCE_MS);
}

function handleVisibilityChangeForWorker() {
  if (document.hidden) {
    lastHiddenAt = Date.now();
    return;
  }

  if (Date.now() - lastHiddenAt >= RECONNECT_AFTER_HIDDEN_MS) {
    scheduleForceReconnect('visibilitychange');
  }
}

function handlePageShowForWorker(e: PageTransitionEvent) {
  if (document.hidden) {
    return;
  }

  const hiddenDurationMs = lastHiddenAt ? Date.now() - lastHiddenAt : 0;
  if (e.persisted || hiddenDurationMs >= RECONNECT_AFTER_HIDDEN_MS) {
    scheduleForceReconnect(e.persisted ? 'pageshow-bfcache' : 'pageshow');
  }
}

function handlePageResumeForWorker() {
  if (document.hidden) {
    return;
  }

  const hiddenDurationMs = lastHiddenAt ? Date.now() - lastHiddenAt : 0;
  if (hiddenDurationMs >= RECONNECT_AFTER_HIDDEN_MS) {
    scheduleForceReconnect('resume');
  }
}

function handlePageFreezeForWorker() {
  lastHiddenAt = Date.now();
}

function handleShellLifecycleEvent(event: ShellLifecycleEvent) {
  if (event.type === 'background') {
    lastHiddenAt = Date.now();
    return;
  }

  if (event.type === 'heartbeat') {
    void ensureWorkerPing();
    return;
  }

  if (event.type === 'foreground') {
    void ensureWorkerPing();
    scheduleForceReconnect('shell-foreground');
    return;
  }

  void ensureWorkerPing();
  scheduleForceReconnect(`shell-network:${event.networkType}`);
}

function handleOnlineForWorker() {
  void ensureWorkerPing();
  scheduleForceReconnect('online');
}

function rejectInflightWorkerRequests(reason: string) {
  const states = [...requestStates.values()].filter((state) => state.via === 'worker');

  states.forEach((state) => {
    requestStates.delete(state.messageId);

    if (state.callback && requestStatesByCallback.get(state.callback) === state) {
      requestStatesByCallback.delete(state.callback);
    }
  });

  states.forEach((state) => {
    try {
      state.reject(new Error(reason));
    } catch (err) {
    }
  });

  pendingPayloads = [];
}

function teardownWorker(reason: string) {
  recordClientDiagnosticEvent({ type: 'worker_terminated', reason });

  worker?.terminate();
  worker = undefined;
  workerCreatedAt = 0;
  isWorkerWired = false;
  isInited = false;
  isInitializing = false;
  initGeneration += 1;

  rejectInflightWorkerRequests(WORKER_TERMINATED_ERROR);

  updateCallback({ '@type': 'requestReconnectApi' });
}

let isHealthCheckRunning = false;

function setupWorkerHealthCheck() {
  if (isHealthCheckAttached) return;
  isHealthCheckAttached = true;

  window.addEventListener('focus', handleFocusForHealthCheck);
  document.addEventListener('visibilitychange', handleVisibilityForHealthCheck);
}

function handleFocusForHealthCheck() {
  void ensureWorkerPing();
}

function handleVisibilityForHealthCheck() {
  if (document.hidden) return;
  void ensureWorkerPing();
}

function pingOnce(): Promise<boolean> {
  const startedPingAt = Date.now();
  const { messageId, promise } = makeRequestWithId({ type: 'ping' });

  let isRttRecorded = false;
  const recordRtt = (success: boolean) => {
    if (isRttRecorded) return;
    isRttRecorded = true;
    recordClientDiagnosticEvent({
      type: 'worker_ping_rtt',
      duration: Date.now() - startedPingAt,
      success,
    });
  };

  return Promise.race([
    promise.then(() => {
      recordRtt(true);
      return true;
    }, () => {
      recordRtt(false);
      return false;
    }),
    pause(HEALTH_CHECK_TIMEOUT).then(() => {
      recordRtt(false);
      requestStates.get(messageId)?.reject(new Error('HEALTH_CHECK_TIMEOUT'));
      requestStates.delete(messageId);
      return false;
    }),
  ]);
}

async function ensureWorkerPing() {
  if (isHealthCheckRunning) return;
  if (!worker) return;
  if (Date.now() - startedAt < HEALTH_CHECK_MIN_DELAY) return;
  if (Date.now() - workerCreatedAt < WORKER_WARMUP_MS) return;
  if (document.hidden) return;

  isHealthCheckRunning = true;

  try {
    for (let i = 0; i < HEALTH_CHECK_MISSES_BEFORE_KILL; i++) {
      if (!worker) return;
      if (await pingOnce()) return;

      if (i < HEALTH_CHECK_MISSES_BEFORE_KILL - 1) {
        await pause(HEALTH_CHECK_RETRY_DELAY);
      }
    }

    if (!worker) return;

    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.error('[Connector] Worker health check failed, terminating');
    }

    teardownWorker('health_check_timeout');
  } finally {
    isHealthCheckRunning = false;
  }
}
